import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Localized, useLocalization } from '@fluent/react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { loadImportedDocumentById, ImportedDocument } from '../importedDocumentsApi';
import { EditDocumentCounterpartyDialog } from '../../components/EditDocumentCounterpartyDialog';
import { CounterpartyDetails, loadCounterpartyDetails } from '../../../counterparty/counterpartyApi';
import { updateCounterparty } from '../../documentsApi';
import { unwrapResult } from '@reduxjs/toolkit';
import { formatCountryLabel } from '../../../administration/companyProfile/countriesApi';
import { selectSettings } from '../../../settings/settingsSlice';

type ImportedDocumentCounterpartyCardProps = {
    document: ImportedDocument;
};

export const ImportedDocumentCounterpartyCard = ({ document }: ImportedDocumentCounterpartyCardProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const { locale } = useAppSelector(selectSettings);
    const navigate = useNavigate();
    const [editOpen, setEditOpen] = useState(false);
    const [details, setDetails] = useState<CounterpartyDetails | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [removing, setRemoving] = useState(false);

    const publicId = document.counterparty?.publicId?.trim() || "";

    useEffect(() => {
        if (!publicId) {
            setDetails(null);
            return;
        }

        setLoadingDetails(true);
        dispatch(loadCounterpartyDetails({ publicId }))
            .unwrap()
            .then((payload) => setDetails(payload))
            .catch(() => setDetails(null))
            .finally(() => setLoadingDetails(false));
    }, [dispatch, publicId]);

    const counterpartyEditPath = publicId
        ? `/suppliercounterparts/edit/${publicId}`
        : null;

    const fallbackCaretaker = useMemo(() => {
        const primary = details?.people?.find((p) => p.isPrimary) ?? details?.people?.[0];
        if (!primary) return null;
        return `${primary.firstName ?? ''} ${primary.lastName ?? ''}`.trim() || null;
    }, [details]);

    const displayName = details?.displayName ?? document.counterparty?.displayName ?? null;
    const countryCode = details?.countryCode ?? document.counterparty?.countryCode ?? null;
    const countryLabel = formatCountryLabel(countryCode, locale);
    const accountManagerName = details?.accountManagerFullName?.trim() || fallbackCaretaker || '-';
    const accountManagerLabel = l10n.getString('imported-documents-counterparty-account-manager', { name: accountManagerName }, `Account manager: ${accountManagerName}`);

    const hasLinkedCounterparty = !!publicId;

    const handleRemoveCounterparty = () => {
        setRemoving(true);
        dispatch(updateCounterparty({ documentId: String(document.id), counterpartyId: null }))
            .then(unwrapResult)
            .then(() => dispatch(loadImportedDocumentById(document.id)))
            .finally(() => setRemoving(false));
    };

    return (
        <>
            <Paper elevation={3} sx={{ p: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ px: 0.5, mb: 1, fontWeight: 700 }}>
                    <Localized id="imported-documents-counterparty-title">
                        <span>_Counterparty</span>
                    </Localized>
                </Typography>
                <Box
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.paper} 100%)`,
                    })}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        {hasLinkedCounterparty ? (
                            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                    <StorefrontIcon fontSize="small" color="primary" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                        {displayName || "-"}
                                    </Typography>
                                    {loadingDetails && <CircularProgress size={14} />}
                                </Stack>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        size="small"
                                        icon={<PublicIcon />}
                                        label={countryLabel}
                                        variant="outlined"
                                        sx={(theme) => ({
                                            borderColor: theme.palette.info.main,
                                            '& .MuiChip-icon': {
                                                color: theme.palette.info.main,
                                            },
                                        })}
                                    />
                                    <Chip
                                        size="small"
                                        icon={<PersonIcon />}
                                        label={accountManagerLabel}
                                        variant="outlined"
                                        sx={(theme) => ({
                                            borderColor: theme.palette.success.main,
                                            '& .MuiChip-icon': {
                                                color: theme.palette.success.main,
                                            },
                                        })}
                                    />
                                </Stack>
                            </Stack>
                        ) : (
                            <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ py: 1.5, flex: 1 }}>
                                <LinkOffIcon color="disabled" />
                                <Typography variant="body2" color="text.secondary" align="center">
                                    <Localized id="imported-documents-counterparty-empty-title">
                                        <span>_No linked company for this document</span>
                                    </Localized>
                                </Typography>
                                <Typography variant="caption" color="text.secondary" align="center">
                                    <Localized id="imported-documents-counterparty-empty-description">
                                        <span>_Use the pencil icon to assign a counterparty.</span>
                                    </Localized>
                                </Typography>
                            </Stack>
                        )}

                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                            <Tooltip title={hasLinkedCounterparty
                                ? l10n.getString('imported-documents-counterparty-change', null, 'Change counterparty')
                                : l10n.getString('imported-documents-counterparty-assign', null, 'Assign counterparty')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        sx={{ border: 1, borderColor: 'divider' }}
                                        onClick={() => setEditOpen(true)}
                                        disabled={removing}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {hasLinkedCounterparty && (
                                <Tooltip title={l10n.getString('imported-documents-counterparty-open', null, 'Go to counterparty')}>
                                    <IconButton
                                        size="small"
                                        sx={{ border: 1, borderColor: 'divider' }}
                                        onClick={() => navigate(counterpartyEditPath!)}
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            {editOpen && (
                <EditDocumentCounterpartyDialog
                    documentId={String(document.id)}
                    value={document.counterparty?.publicId ?? ""}
                    onSuccess={() => {
                        setEditOpen(false);
                        dispatch(loadImportedDocumentById(document.id));
                    }}
                    onCancel={() => setEditOpen(false)}
                />
            )}
        </>
    );
};
